/**
 * Boli Live - Seller Dashboard
 *
 * Analytics, auction, and product management for sellers.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Users,
  DollarSign,
  Package,
  Plus,
  MoreVertical,
  ShoppingBag,
  Settings,
  Loader2,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { cn, formatPKR } from '../lib/utils';
import { useAuthStore } from '../lib/stores/auth-store';
import { getProductService } from '@boli/core';
import type { Product } from '@boli/types';

const recentAuctions = [
  { id: 1, title: 'Vintage Rolex Datejust', price: 850000, status: 'live', bids: 12, views: 240 },
  { id: 2, title: 'iPhone 15 Pro Max', price: 420000, status: 'scheduled', bids: 0, views: 45 },
  { id: 3, title: 'Sony PlayStation 5', price: 125000, status: 'ended', bids: 28, views: 890 },
];

export default function SellerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  // Fetch seller's products
  useEffect(() => {
    const fetchProducts = async () => {
      if (!user?.id) return;

      try {
        const productService = getProductService();
        const result = await productService.getProducts({ sellerId: user.id, limit: 5 });
        setProducts(result.products);
      } catch {
        // Failed to fetch products
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [user?.id]);

  // Calculate product stats
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.status === 'active').length;

  // Generate stats array with real product data
  const stats = [
    {
      label: 'Total Sales',
      value: 'PKR 124K',
      change: '+12%',
      icon: DollarSign,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'Active Auctions',
      value: '3',
      change: 'Live now',
      icon: Package,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
    {
      label: 'Products',
      value: String(totalProducts),
      change: `${activeProducts} active`,
      icon: ShoppingBag,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
    },
    {
      label: 'Total Views',
      value: '1.2K',
      change: '+8%',
      icon: Users,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
    },
  ];

  return (
    <div className="min-h-screen pb-24 pt-4">
      <div className="px-4 mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back, {user?.firstName || 'Seller'}</p>
      </div>

      {/* Stats Grid */}
      <div className="px-4 grid grid-cols-2 gap-3 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            className={cn("bg-card border border-border rounded-2xl p-4 shadow-sm", index === 0 && "col-span-2")}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={cn("p-2 rounded-xl", stat.bg)}>
                <stat.icon className={cn("w-5 h-5", stat.color)} />
              </div>
              <span className={cn("text-xs font-medium px-2 py-1 rounded-full bg-secondary text-secondary-foreground")}>
                {stat.change}
              </span>
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{stat.label}</p>
              <h3 className="text-2xl font-bold font-display mt-1 text-foreground">{stat.value}</h3>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="px-4 mb-8">
        <h2 className="text-lg font-bold text-foreground mb-4">Quick Actions</h2>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
          <button
            onClick={() => navigate('/go-live')}
            className="flex-shrink-0 w-32 bg-primary text-primary-foreground rounded-2xl p-4 flex flex-col items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors active:scale-95"
          >
            <div className="p-2 bg-white/20 rounded-full">
              <Plus className="w-6 h-6" />
            </div>
            <span className="font-semibold text-sm">New Auction</span>
          </button>

          <button
            onClick={() => navigate('/seller/products')}
            className="flex-shrink-0 w-32 bg-card border border-border text-foreground rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-muted transition-colors active:scale-95"
          >
            <div className="p-2 bg-secondary rounded-full">
              <ShoppingBag className="w-6 h-6 text-purple-500" />
            </div>
            <span className="font-semibold text-sm">Products</span>
          </button>

          <button
            onClick={() => navigate('/seller/settings')}
            className="flex-shrink-0 w-32 bg-card border border-border text-foreground rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-muted transition-colors active:scale-95"
          >
            <div className="p-2 bg-secondary rounded-full">
              <Settings className="w-6 h-6 text-gray-500" />
            </div>
            <span className="font-semibold text-sm">Store Settings</span>
          </button>

          <button className="flex-shrink-0 w-32 bg-card border border-border text-foreground rounded-2xl p-4 flex flex-col items-center gap-2 hover:bg-muted transition-colors active:scale-95">
            <div className="p-2 bg-secondary rounded-full">
              <TrendingUp className="w-6 h-6 text-emerald-500" />
            </div>
            <span className="font-semibold text-sm">Analytics</span>
          </button>
        </div>
      </div>

      {/* Recent Auctions */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Recent Auctions</h2>
          <button className="text-primary text-sm font-medium hover:underline">View All</button>
        </div>

        <div className="space-y-3">
          {recentAuctions.map((item, index) => (
            <motion.div
              key={item.id}
              className="bg-card border border-border rounded-xl p-3 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                 {/* Mock Image Placeholder */}
                 <Package className="w-6 h-6 text-muted-foreground" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-foreground text-sm truncate">{item.title}</h4>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span className="font-medium text-foreground">PKR {item.price.toLocaleString()}</span>
                  <span>•</span>
                  <span>{item.bids} bids</span>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <span
                  className={cn(
                    'text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide',
                    item.status === 'live' &&
                      'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400',
                    item.status === 'scheduled' &&
                      'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
                    item.status === 'ended' &&
                      'bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400'
                  )}
                >
                  {item.status}
                </span>
                <button className="p-1 hover:bg-secondary rounded">
                  <MoreVertical className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Products */}
      <div className="px-4 mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Recent Products</h2>
          <Link to="/seller/products" className="text-primary text-sm font-medium hover:underline">
            View All
          </Link>
        </div>

        {isLoadingProducts ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : products.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <ShoppingBag className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">No products yet</p>
            <Link
              to="/seller/products/new"
              className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline"
            >
              <Plus className="w-4 h-4" />
              Add your first product
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className="bg-card border border-border rounded-xl p-3 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                onClick={() => navigate(`/seller/products/${product.id}/edit`)}
              >
                <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground text-sm truncate">{product.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="font-medium text-foreground">{formatPKR(product.price)}</span>
                    <span>•</span>
                    <span>{product.stock} in stock</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <span
                    className={cn(
                      'text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide',
                      product.status === 'active' &&
                        'bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400',
                      product.status === 'draft' &&
                        'bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400',
                      product.status === 'paused' &&
                        'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
                      product.status === 'sold_out' &&
                        'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400',
                      product.status === 'archived' &&
                        'bg-gray-100 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400'
                    )}
                  >
                    {product.status.replace('_', ' ')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
